import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Shield, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StageGateRule } from '../types/stageGate';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STAGE_OPTIONS = ['NEW', 'CONTACTED', 'DISCUSSION', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
const CATEGORY_OPTIONS = [
  'PROPOSAL', 'CONTRACT', 'IMPLEMENTATION', 'INSTALLATION', 'TRAINING',
  'SUPPORT', 'DOCUMENTATION', 'LICENSE', 'SERVICE', 'EQUIPMENT',
  'CONSULTATION', 'MAINTENANCE', 'OTHER'
];

export const StageGateConfigPage: React.FC = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState<StageGateRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create rule form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTargetStage, setNewTargetStage] = useState('NEGOTIATION');
  const [newRequiredCategory, setNewRequiredCategory] = useState('CONTRACT');
  const [newDescription, setNewDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Plan templates section
  const [showPlanTemplates, setShowPlanTemplates] = useState(false);
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [planTemplates, setPlanTemplates] = useState<any[]>([]);
  const [deliverableTemplates, setDeliverableTemplates] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const getToken = () => localStorage.getItem('token');

  const fetchRules = async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_URL}/stage-gates`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) setRules(Array.isArray(data.data) ? data.data : []);
      else {
        setRules([]);
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load gate rules');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateRule = async () => {
    if (!newTargetStage || !newRequiredCategory) return;
    try {
      setIsCreating(true);
      const res = await fetch(`${API_URL}/stage-gates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          targetStage: newTargetStage,
          requiredCategory: newRequiredCategory,
          description: newDescription || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        setRules(prev => [...prev, data.data]);
        setShowCreateForm(false);
        setNewDescription('');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to create gate rule');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleRule = async (rule: StageGateRule) => {
    try {
      const res = await fetch(`${API_URL}/stage-gates/${rule.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ isActive: !rule.isActive })
      });
      const data = await res.json();
      if (data.success) {
        setRules(prev => prev.map(r => r.id === rule.id ? data.data : r));
      }
    } catch (err) {
      setError('Failed to update gate rule');
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Delete this gate rule? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/stage-gates/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) {
        setRules(prev => prev.filter(r => r.id !== ruleId));
      }
    } catch (err) {
      setError('Failed to delete gate rule');
    }
  };

  // Group rules by targetStage
  const rulesByStage = rules.reduce<Record<string, StageGateRule[]>>((acc, rule) => {
    if (!acc[rule.targetStage]) acc[rule.targetStage] = [];
    acc[rule.targetStage].push(rule);
    return acc;
  }, {});

  // Plan templates section
  const loadPlanTemplatesSection = async () => {
    if (plans.length > 0) return; // Already loaded
    try {
      setIsLoadingPlans(true);
      const [plansRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/subscription-plans`, { headers: { Authorization: `Bearer ${getToken()}` } }),
        fetch(`${API_URL}/deliverable-templates`, { headers: { Authorization: `Bearer ${getToken()}` } })
      ]);
      const [plansData, templatesData] = await Promise.all([plansRes.json(), templatesRes.json()]);
      if (plansData.success) setPlans(plansData.data || []);
      if (templatesData.success) setDeliverableTemplates(templatesData.data || []);
      if (plansData.data?.length > 0) setSelectedPlanId(plansData.data[0].id);
    } catch (err) {
      setError('Failed to load plans or templates');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const loadPlanTemplates = async (planId: string) => {
    if (!planId) return;
    try {
      const res = await fetch(`${API_URL}/subscription-plans/${planId}/templates`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (data.success) setPlanTemplates(data.data || []);
    } catch (err) {
      setError('Failed to load plan templates');
    }
  };

  useEffect(() => {
    if (selectedPlanId) loadPlanTemplates(selectedPlanId);
  }, [selectedPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssociateTemplate = async () => {
    if (!selectedPlanId || !selectedTemplateId) return;
    try {
      const res = await fetch(`${API_URL}/subscription-plans/${selectedPlanId}/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ templateId: selectedTemplateId })
      });
      const data = await res.json();
      if (data.success) {
        loadPlanTemplates(selectedPlanId);
        setSelectedTemplateId('');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to associate template');
    }
  };

  const handleRemovePlanTemplate = async (templateId: string) => {
    if (!selectedPlanId) return;
    try {
      await fetch(`${API_URL}/subscription-plans/${selectedPlanId}/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      loadPlanTemplates(selectedPlanId);
    } catch (err) {
      setError('Failed to remove template association');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Stage Gate Configuration</h1>
              <p className="text-sm text-gray-500">Configure mandatory deliverable requirements per pipeline stage</p>
            </div>
          </div>
          {user?.role === 'CEO' && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Gate Rule
            </button>
          )}
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Create Rule Form */}
        {showCreateForm && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-blue-900">New Gate Rule</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Target Stage</label>
                <select
                  value={newTargetStage}
                  onChange={e => setNewTargetStage(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {STAGE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Required Deliverable Category</label>
                <select
                  value={newRequiredCategory}
                  onChange={e => setNewRequiredCategory(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
              <input
                type="text"
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                placeholder="e.g. Signed contract required before negotiation"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateRule}
                disabled={isCreating}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create Rule'}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Gate Rules List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading gate rules...</div>
          ) : rules.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
              <Shield className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No gate rules configured yet.</p>
              <p className="text-xs text-gray-400 mt-1">Add a rule to require deliverable categories before stage advancement.</p>
            </div>
          ) : (
            Object.entries(rulesByStage).map(([stage, stageRules]) => (
              <div key={stage} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <Shield className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-700">Required before: {stage}</h3>
                  <span className="ml-auto text-xs text-gray-400">{stageRules.length} rule{stageRules.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {stageRules.map(rule => (
                    <div key={rule.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          rule.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {rule.requiredCategory}
                        </span>
                        {rule.description && (
                          <span className="text-sm text-gray-500">{rule.description}</span>
                        )}
                      </div>
                      {user?.role === 'CEO' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleRule(rule)}
                            className="text-gray-400 hover:text-gray-600"
                            title={rule.isActive ? 'Disable rule' : 'Enable rule'}
                          >
                            {rule.isActive
                              ? <ToggleRight className="h-5 w-5 text-green-500" />
                              : <ToggleLeft className="h-5 w-5 text-gray-400" />
                            }
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="text-gray-400 hover:text-red-500"
                            title="Delete rule"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Plan Templates Section (collapsible) */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => {
              setShowPlanTemplates(!showPlanTemplates);
              if (!showPlanTemplates) loadPlanTemplatesSection();
            }}
            className="w-full flex items-center justify-between bg-gray-50 px-4 py-3 border-b border-gray-200 hover:bg-gray-100"
          >
            <span className="text-sm font-semibold text-gray-700">Subscription Plan Templates (GATE-05)</span>
            {showPlanTemplates ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
          </button>

          {showPlanTemplates && (
            <div className="p-4 space-y-4">
              <p className="text-xs text-gray-500">
                Associate deliverable templates with subscription plans. When a deal is won with a plan,
                these templates auto-create deliverables for the lead.
              </p>

              {isLoadingPlans ? (
                <div className="text-center py-4 text-gray-500 text-sm">Loading plans...</div>
              ) : (
                <>
                  {/* Plan selector */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Select Plan</label>
                    <select
                      value={selectedPlanId}
                      onChange={e => setSelectedPlanId(e.target.value)}
                      className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="">-- Select a plan --</option>
                      {plans.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Current plan templates */}
                  {selectedPlanId && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-medium text-gray-700">
                        Templates in this plan ({planTemplates.length})
                      </h4>
                      {planTemplates.length === 0 ? (
                        <p className="text-xs text-gray-400">No templates associated yet.</p>
                      ) : (
                        <div className="space-y-1">
                          {planTemplates.map(pt => (
                            <div key={pt.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                              <div>
                                <span className="text-sm font-medium text-gray-800">{pt.template?.name || 'Unknown'}</span>
                                <span className="ml-2 text-xs text-gray-400">{pt.template?.category || 'N/A'}</span>
                              </div>
                              {user?.role === 'CEO' && (
                                <button
                                  onClick={() => handleRemovePlanTemplate(pt.templateId)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add template association */}
                      {user?.role === 'CEO' && (
                        <div className="flex gap-2 pt-2">
                          <select
                            value={selectedTemplateId}
                            onChange={e => setSelectedTemplateId(e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                          >
                            <option value="">-- Add a template --</option>
                            {deliverableTemplates
                              .filter(t => !planTemplates.find(pt => pt.templateId === t.id))
                              .map(t => (
                                <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                              ))}
                          </select>
                          <button
                            onClick={handleAssociateTemplate}
                            disabled={!selectedTemplateId}
                            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Info box about how gates work */}
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
          <p className="font-medium mb-1">How Stage Gates Work</p>
          <ul className="text-xs space-y-1 text-blue-700 list-disc list-inside">
            <li>A gate rule requires a deliverable of the specified category to exist for a lead before it can advance to that stage.</li>
            <li>Disabled rules are not enforced but are preserved for reference.</li>
            <li>Gates only apply at transition time — existing leads are not retroactively affected.</li>
            <li>CEO can bypass a gate with a reason, which is recorded in the audit log.</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StageGateConfigPage;
